import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Divider, Link, Skeleton, Stack, Typography } from '@mui/material';
import { livyJson } from 'src/lib/livyApi';
const SEVERITY_COLOR = {
  low: 'default',
  normal: 'default',
  high: 'warning',
  urgent: 'error'
};
const STATUS_COLOR = {
  raised: 'info',
  confirmed: 'success',
  dismissed: 'default'
};
const SECTION_LABEL = {
  fontSize: 10.5,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  color: 'text.secondary',
  mb: '3px'
};

/**
 * A list and a decision, not a form. Requests are raised by asking Livy, so a second place to type
 * one would be a second way to describe the same problem.
 *
 * The decision lives here rather than in the chat because the assistant has no way to record a yes:
 * confirming in the conversation looped, each confirmation raising the request again.
 */
export function RequestsTab() {
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    requests = _useState2[0],
    setRequests = _useState2[1];
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    error = _useState4[0],
    setError = _useState4[1];
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    busy = _useState6[0],
    setBusy = _useState6[1];
  const load = useCallback(() => {
    livyJson('/requests').then(d => setRequests(d.requests)).catch(e => setError(e.message));
  }, []);
  useEffect(load, [load]);
  const decide = async (sk, decision) => {
    setBusy(sk);
    setError(null);
    try {
      const _await$livyJson = await livyJson('/requests', {
          method: 'POST',
          // livyJson serialises the body itself; passing a string here double-encoded it and the
          // server parsed a string rather than an object, so every decision came back a 400.
          body: {
            sk,
            decision
          }
        }),
        request = _await$livyJson.request,
        jiraError = _await$livyJson.jiraError;
      if (jiraError) setError("Recorded as ".concat(decision, ", but Jira rejected it: ").concat(jiraError));
      setRequests(list => (list !== null && list !== void 0 ? list : []).map(r => r.sk === sk ? _objectSpread(_objectSpread({}, r), request) : r));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };
  if (error && !requests) return /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    sx: {
      mt: '16px'
    }
  }, error);
  if (!requests) return /*#__PURE__*/React.createElement(Stack, {
    spacing: "8px",
    sx: {
      mt: '16px'
    }
  }, [0, 1, 2].map(i => /*#__PURE__*/React.createElement(Skeleton, {
    key: i,
    height: 64
  })));
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      pt: '16px',
      maxWidth: 860
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary",
    sx: {
      mb: '16px'
    }
  }, "Raised by asking Livy, in this console or in support. Nothing here has reached engineering: confirming files it, and only then are the implicated services filled in from the knowledge graph."), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    sx: {
      mb: '12px'
    },
    onClose: () => setError(null)
  }, error), requests.length === 0 ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, "Nothing raised yet. Ask Livy to file a bug and it will write one up for you.") : /*#__PURE__*/React.createElement(Stack, {
    spacing: "10px"
  }, requests.map(r => {
    var _r$sk, _SEVERITY_COLOR$r$sev, _STATUS_COLOR, _r$status, _r$status2, _r$raisedBy, _r$problem, _r$status3;
    return /*#__PURE__*/React.createElement(Box, {
      key: (_r$sk = r.sk) !== null && _r$sk !== void 0 ? _r$sk : "".concat(r.at, "-").concat(r.title),
      sx: {
        border: 1,
        borderColor: 'divider',
        borderRadius: '10px',
        p: '14px'
      }
    }, /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      spacing: "8px",
      alignItems: "center",
      sx: {
        mb: '6px',
        flexWrap: 'wrap',
        rowGap: '6px'
      }
    }, /*#__PURE__*/React.createElement(Chip, {
      size: "small",
      variant: "outlined",
      label: r.type
    }), /*#__PURE__*/React.createElement(Chip, {
      size: "small",
      variant: "outlined",
      color: (_SEVERITY_COLOR$r$sev = SEVERITY_COLOR[r.severity]) !== null && _SEVERITY_COLOR$r$sev !== void 0 ? _SEVERITY_COLOR$r$sev : 'default',
      label: r.severity
    }), /*#__PURE__*/React.createElement(Chip, {
      size: "small",
      color: (_STATUS_COLOR = STATUS_COLOR[(_r$status = r.status) !== null && _r$status !== void 0 ? _r$status : 'raised']) !== null && _STATUS_COLOR !== void 0 ? _STATUS_COLOR : 'default',
      variant: r.status === 'confirmed' ? 'filled' : 'outlined',
      label: (_r$status2 = r.status) !== null && _r$status2 !== void 0 ? _r$status2 : 'raised'
    }), r.jiraKey && /*#__PURE__*/React.createElement(Chip, {
      size: "small",
      color: "primary",
      label: r.jiraKey
    }), r.estimate && /*#__PURE__*/React.createElement(Chip, {
      size: "small",
      variant: "outlined",
      label: "~".concat(r.estimate, " with Claude Code")
    })), /*#__PURE__*/React.createElement(Typography, {
      sx: {
        fontSize: 14.5,
        fontWeight: 600,
        mb: '3px'
      }
    }, r.title), /*#__PURE__*/React.createElement(Typography, {
      sx: {
        fontSize: 11.5,
        color: 'text.secondary',
        mb: '10px'
      }
    }, (_r$raisedBy = r.raisedBy) !== null && _r$raisedBy !== void 0 ? _r$raisedBy : 'unknown', r.raisedByRole && r.raisedByRole !== 'unknown' ? " \xB7 ".concat(r.raisedByRole) : '', " \xB7", ' ', new Date(r.at).toLocaleString(), r.conversationId && /*#__PURE__*/React.createElement(React.Fragment, null, ' · ', /*#__PURE__*/React.createElement(Link, {
      href: "?tab=usage&conversation=".concat(encodeURIComponent(r.conversationId)),
      underline: "hover"
    }, "view chat")), r.decidedBy && " \xB7 ".concat(r.status, " by ").concat(r.decidedBy)), /*#__PURE__*/React.createElement(Stack, {
      spacing: "10px"
    }, [['Problem', (_r$problem = r.problem) !== null && _r$problem !== void 0 ? _r$problem : r.description], ['Plan', r.plan], ['Success criteria', r.successCriteria]].map(_ref => {
      let _ref2 = _slicedToArray(_ref, 2),
        label = _ref2[0],
        value = _ref2[1];
      return value ? /*#__PURE__*/React.createElement(Box, {
        key: label
      }, /*#__PURE__*/React.createElement(Typography, {
        sx: SECTION_LABEL
      }, label), /*#__PURE__*/React.createElement(Typography, {
        variant: "body2",
        sx: {
          whiteSpace: 'pre-wrap'
        }
      }, value)) : null;
    })), r.repos && r.repos.length > 0 && /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      spacing: "6px",
      sx: {
        flexWrap: 'wrap',
        rowGap: '6px',
        mt: '10px'
      }
    }, r.repos.map(repo => /*#__PURE__*/React.createElement(Chip, {
      key: repo,
      size: "small",
      variant: "outlined",
      label: repo,
      sx: {
        height: 20
      }
    }))), ((_r$status3 = r.status) !== null && _r$status3 !== void 0 ? _r$status3 : 'raised') === 'raised' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Divider, {
      sx: {
        my: '12px'
      }
    }), /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      spacing: "8px"
    }, /*#__PURE__*/React.createElement(Button, {
      size: "small",
      variant: "contained",
      disabled: busy === r.sk,
      onClick: () => void decide(r.sk, 'confirmed'),
      sx: {
        textTransform: 'none'
      }
    }, "Confirm and file"), /*#__PURE__*/React.createElement(Button, {
      size: "small",
      disabled: busy === r.sk,
      onClick: () => void decide(r.sk, 'dismissed'),
      sx: {
        textTransform: 'none'
      }
    }, "Dismiss"))));
  })));
}